import { api } from '../api';
import { Banner, EmptyState, Pill, Spinner, useAsync } from '../components/Common';
import { relativeTime } from '../format';

interface ReportSummary {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  version: number;
  lastRun?: { id: string; started_at: string; status: string; duration_ms: number } | null;
}

export function Catalogue({ onOpen }: { onOpen: (slug: string) => void }) {
  const { data, error, loading } = useAsync(() => api.get<ReportSummary[]>('/api/reports'));

  if (loading && !data) return <Spinner label="Loading reports…" />;
  if (error) return <Banner tone="error" title="Could not load the report list. ">{error}</Banner>;

  const reports = data ?? [];
  if (!reports.length) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Reports</h1>
            <p>Reports you have access to run.</p>
          </div>
        </div>
        <EmptyState title="No reports available to you yet">
          An editor needs to build one, or grant your role access to an existing report.
        </EmptyState>
      </>
    );
  }

  const categories = [...new Set(reports.map((r) => r.category ?? 'General'))];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <p>Pick a report, set the period, and run it. Results can be exported to Excel or a branded PDF.</p>
        </div>
      </div>

      {categories.map((category) => (
        <section key={category} style={{ marginBottom: 26 }}>
          {/* Sentence case: table headers are the only ALL CAPS in the product. */}
          <h2 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--lilac-dark)', margin: '0 0 10px' }}>
            {category}
          </h2>
          <div className="grid-cards">
            {reports
              .filter((r) => (r.category ?? 'General') === category)
              .map((report) => (
                <button className="card report-card" key={report.slug} onClick={() => onOpen(report.slug)}>
                  <h3>{report.name}</h3>
                  {report.description && <p>{report.description}</p>}
                  <div className="foot">
                    {report.lastRun ? (
                      <>
                        Last run {relativeTime(report.lastRun.started_at)}{' '}
                        {report.lastRun.status !== 'ok' && (
                          <Pill tone={report.lastRun.status === 'error' ? 'bad' : 'warn'}>{report.lastRun.status}</Pill>
                        )}
                      </>
                    ) : (
                      'Not run yet'
                    )}
                  </div>
                </button>
              ))}
          </div>
        </section>
      ))}
    </>
  );
}
